/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable } from '@angular/core'
import { FeatureToggleConfig, FeatureToggleEnum } from './feature-toggle.service'
import { FeatureToggles } from '../configuration/config.interface'

export interface AssignmentHalves {
  key: string
  val: boolean
}

@Injectable({
  providedIn: 'root',
})
export class FeatureToggleHelperService {
  public static ftPrefix = 'feature'

  getAssignmentHalves(fragment: string): AssignmentHalves[] {
    const result: AssignmentHalves[] = []
    if (!fragment) {
      return result
    }

    const assignmentParts = fragment.split(';')
    assignmentParts.forEach((assignmentPart) => {
      const assignmentHalves = assignmentPart.split('=')
      if (assignmentHalves.length === 2 && assignmentHalves[0].indexOf(FeatureToggleHelperService.ftPrefix) === 0) {
        result.push({
          key: assignmentHalves[0].substring(FeatureToggleHelperService.ftPrefix.length),
          val: assignmentHalves[1] === '1',
        })
      }
    })

    return result
  }

  isFeatureEnabled<T, U>(featureToggles: FeatureToggles, featureToggle: string, disabled: T, enabled: U, invert = false): T | U {
    const contained = (featureToggles.enabled ?? []).indexOf(featureToggle) > -1
    if ((invert && !contained) || (!invert && contained)) {
      return enabled
    }

    return disabled
  }

  updateFeatureToggles(
    featureToggles: FeatureToggles,
    featureToggleEnum: FeatureToggleEnum,
    assignmentHalvesSet: AssignmentHalves[],
  ): FeatureToggles {
    const modifiedFeatureToggles = {
      enabled: [...(featureToggles.enabled ?? [])],
      disabled: featureToggles.disabled,
    }

    assignmentHalvesSet.forEach((assignmentHalves) => {
      const featureToggle = assignmentHalves.key
      if (this.isFeatureToggle(featureToggle, featureToggleEnum)) {
        const idx = modifiedFeatureToggles.enabled.indexOf(featureToggle)
        if (assignmentHalves.val) {
          // add feature if not already activated
          if (idx < 0) {
            modifiedFeatureToggles.enabled.push(featureToggle)
          }
          return
        }

        if (idx > -1) {
          // remove feature
          modifiedFeatureToggles.enabled.splice(idx, 1)
        }
      }
    })

    return modifiedFeatureToggles
  }

  getFeatureToggleEnum(featureToggleEnum: FeatureToggleEnum | null, featureToggleConfig: FeatureToggleConfig): FeatureToggleEnum | null {
    if (featureToggleEnum !== null) {
      return featureToggleEnum
    }

    return featureToggleConfig?.featureToggleEnum ?? null
  }

  private isFeatureToggle(featureToggle: string, featureToggleEnum: FeatureToggleEnum): boolean {
    return featureToggleEnum[featureToggle] !== undefined
  }
}
